import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface WindmillFlowSchema {
  $schema: string;
  type: string;
  order: string[];
  properties: Record<
    string,
    {
      type: string;
      description: string;
      default?: string | number | boolean;
      nullable?: boolean;
    }
  >;
  required: string[];
}

export interface WindmillFlowData {
  workspace_id: string;
  path: string;
  summary: string;
  description: string;
  value: {
    modules: any[];
    failure_module?: any;
  };
  edited_by: string;
  edited_at: string;
  archived: boolean;
  schema: WindmillFlowSchema;
  extra_perms: Record<string, any>;
}

export type WindmillFlowFormData = Record<string, string | number | boolean>;

export interface WindmillFlowState {
  currentFlow: WindmillFlowData | null;
  formData: WindmillFlowFormData;
  isFormOpen: boolean;
  isLoading: boolean;
  isExecuting: boolean;
  executionResult: any;
  error: string | null;
  validationErrors: Record<string, string>;
  windmillJobId: string | null;
}

const initialState: WindmillFlowState = {
  currentFlow: null,
  formData: {},
  isFormOpen: false,
  isLoading: false,
  isExecuting: false,
  executionResult: null,
  error: null,
  validationErrors: {},
  windmillJobId: null,
};

// Validation function to check if form is valid
export const validateFormData = (
  formData: WindmillFlowFormData,
  schema: WindmillFlowSchema
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  const { properties, required } = schema;

  // Check required fields
  required.forEach(field => {
    if (
      !formData[field] ||
      (typeof formData[field] === 'string' && formData[field] === '') ||
      (typeof formData[field] === 'number' &&
        formData[field] === 0 &&
        !properties[field]?.default)
    ) {
      errors[field] = `${field} is required`;
    }
  });

  // Check field types
  Object.entries(properties).forEach(([field, prop]) => {
    const value = formData[field];
    if (value !== undefined && value !== null && value !== '') {
      if (prop.type === 'number' && isNaN(Number(value))) {
        errors[field] = `${field} must be a number`;
      }
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

const windmillFlowSlice = createSlice({
  name: 'windmillFlow',
  initialState,
  reducers: {
    setCurrentFlow: (state, action: PayloadAction<WindmillFlowData>) => {
      state.currentFlow = action.payload;
      // Initialize form data with default values from schema
      const formData: WindmillFlowFormData = {};
      if (action.payload.schema?.properties) {
        Object.entries(action.payload.schema.properties).forEach(
          ([key, prop]) => {
            if (prop.default !== undefined) {
              formData[key] = prop.default;
            } else if (prop.type === 'string') {
              formData[key] = '';
            } else if (prop.type === 'number') {
              formData[key] = 0;
            } else if (prop.type === 'boolean') {
              formData[key] = false;
            }
          }
        );
      }
      state.formData = formData;
      // Validate the form data after setting it
      if (action.payload.schema) {
        const { errors } = validateFormData(formData, action.payload.schema);
        state.validationErrors = errors;
      }
    },
    updateFormData: (
      state,
      action: PayloadAction<{ field: string; value: any }>
    ) => {
      const { field, value } = action.payload;
      state.formData[field] = value;

      // Validate the form data after updating
      if (state.currentFlow?.schema) {
        const { errors } = validateFormData(
          state.formData,
          state.currentFlow.schema
        );
        state.validationErrors = errors;
      }
    },
    setFormOpen: (state, action: PayloadAction<boolean>) => {
      state.isFormOpen = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsExecuting: (state, action: PayloadAction<boolean>) => {
      state.isExecuting = action.payload;
    },
    setExecutionResult: (state, action: PayloadAction<any>) => {
      state.executionResult = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.validationErrors = action.payload;
    },
    resetForm: state => {
      if (state.currentFlow?.schema?.properties) {
        const formData: WindmillFlowFormData = {};
        Object.entries(state.currentFlow.schema.properties).forEach(
          ([key, prop]) => {
            if (prop.default !== undefined) {
              formData[key] = prop.default;
            } else if (prop.type === 'string') {
              formData[key] = '';
            } else if (prop.type === 'number') {
              formData[key] = 0;
            } else if (prop.type === 'boolean') {
              formData[key] = false;
            }
          }
        );
        state.formData = formData;
        // Validate the form data after resetting
        if (state.currentFlow.schema) {
          const { errors } = validateFormData(
            formData,
            state.currentFlow.schema
          );
          state.validationErrors = errors;
        }
      }
      state.error = null;
      state.executionResult = null;
    },
    clearFlow: state => {
      state.currentFlow = null;
      state.formData = {};
      state.isFormOpen = false;
      state.isLoading = false;
      state.isExecuting = false;
      state.executionResult = null;
      state.error = null;
      state.validationErrors = {};
    },
    setWindmillJobId: (state, action: PayloadAction<string | null>) => {
      state.windmillJobId = action.payload;
    },
    clearWindmillJobId: state => {
      state.windmillJobId = null;
    },
  },
});

export const {
  setCurrentFlow,
  updateFormData,
  setFormOpen,
  setIsLoading,
  setIsExecuting,
  setExecutionResult,
  setError,
  setValidationErrors,
  resetForm,
  clearFlow,
  setWindmillJobId,
  clearWindmillJobId,
} = windmillFlowSlice.actions;

export default windmillFlowSlice.reducer;
